import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import React from "react";
import classNames from "components/ui/classNames";

interface MenuActionButtonItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof menuActionButtonVariants> {}

const menuActionButtonVariants = cva(
  classNames(
    "m-0 box-border flex w-full cursor-pointer items-center overflow-hidden rounded-xl",
    "bg-transparent px-2 py-0 text-sm tracking-wider transition-all duration-75 ease-in-out",
    "pointer-events-auto select-none appearance-none",
    "disabled:pointer-events-none",
    "dark:font-medium dark:text-slate-200 dark:hover:bg-slate-950",
  ),
  {
    variants: {
      variant: {
        default: classNames(
          "border border-transparent text-slate-600",
          "dark:text-slate-300",
          "hover:bg-slate-100 hover:font-medium dark:hover:bg-slate-600",
        ),
        primary: classNames(
          "border border-transparent text-slate-600",
          "hover:bg-blue-50 hover:font-medium hover:text-blue-600",
          "dark:text-slate-300",
          "dark:hover:bg-sky-500/20 dark:hover:text-sky-300",
        ),
        danger: classNames(
          "border border-transparent text-slate-600",
          "dark:text-slate-300",
          "hover:bg-red-50 hover:font-medium hover:text-red-700 dark:hover:bg-red-500/20 dark:hover:text-red-400",
        ),
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const MenuActionButtonItem = React.forwardRef<HTMLButtonElement, MenuActionButtonItemProps>(
  ({ variant, children, disabled, className, ...props }, ref) => {
    return (
      <li className="-mx-2">
        <button
          ref={ref}
          type="button"
          disabled={disabled}
          className={classNames(menuActionButtonVariants({ variant }), className)}
          {...props}
        >
          <span className="py-2">{children}</span>
        </button>
      </li>
    );
  },
);

export default MenuActionButtonItem;
